import { useEffect, useRef, useState } from "react";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Info, Check, Tag, ShieldCheck, Loader2, X, Sparkles, Zap, Lock, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { stripeAppearance } from "@/lib/theme";
import { PLAN_COMPARE_COMPACT, tierColumnIndex } from "@/lib/tierFeatures";
import { Input } from "@/components/ui/input";
import BrandLogo from "@/components/BrandLogo";

interface Tier {
  id: number;
  name: string;
  amount_per_month: number;
  amount_per_year: number | null;
  free_listings_access: number | null;
  listing_locks_allowed: number | null;
  advance_access_hours: number | null;
  lock_duration_hours: number | null;
  extra_listing_amount: number | null;
  features_text: string;
}

interface TierSelectionPayload {
  tierId: number;
  subscriptionId?: string;
  promoCode?: string;
}

// Persisted just before stripe.confirmPayment: redirect-based payment methods
// (bank redirects, Cash App Pay, …) navigate the browser away, wiping all React
// state. On return (Stripe appends ?redirect_status=… to the return_url) the host
// page reads this back to finish registration/activation for the already-paid
// subscription. Hosts clear it once the subscription is attached to an account.
export const PENDING_STRIPE_PAYMENT_KEY = "bl_pending_stripe_payment";

export interface PendingStripePayment {
  subscriptionId: string;
  tierId: number;
}

export function readPendingStripePayment(): PendingStripePayment | null {
  try {
    const raw = sessionStorage.getItem(PENDING_STRIPE_PAYMENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.subscriptionId === "string" && typeof parsed?.tierId === "number") return parsed;
  } catch { /* corrupt/legacy value — treat as absent */ }
  return null;
}

interface PromoValidationResponse {
  valid: boolean;
  code: string;
  tier_id: number;
  tier_name: string;
  access_days: number;
}

interface TierSelectorProps {
  registrationEmail: string;
  onTierSelected: (payload: TierSelectionPayload) => void;
  isLoading?: boolean;
  // Direct payment mode: skip the comparison table and open the payment modal
  // straight away for this tier (used by the Choose-plan page's plan cards).
  directTierId?: number | null;
  directInterval?: "month" | "year";
  onClose?: () => void;
  // Signup flow (no account exists yet): the backend rejects an already-registered
  // email BEFORE creating the subscription, so a buyer can never pay and then hit
  // "email already registered" at account creation.
  forRegistration?: boolean;
}

const TIER_META = [
  {
    label: "EXPLORER",
    subtitle: "Starter",
    icon: <Zap className="h-4 w-4" />,
    iconBg: "bg-slate-100 text-slate-600",
    accent: "text-slate-600",
    activeBg: "bg-slate-600",
    colBg: "bg-slate-50/40",
    colBorder: "border-slate-500",
    headerBg: "bg-slate-600",
  },
  {
    label: "OPERATOR",
    subtitle: "Deal Flow",
    badge: "Most popular",
    badgeBg: "bg-amber-500",
    icon: <Sparkles className="h-4 w-4" />,
    iconBg: "bg-amber-100 text-amber-600",
    accent: "text-amber-600",
    activeBg: "bg-amber-500",
    colBg: "bg-amber-50/40",
    colBorder: "border-amber-500",
    headerBg: "bg-amber-500",
  },
];

// Headline feature rows come from @/lib/tierFeatures — the same matrix the
// choose-plan page and the upgrade / benefits dialogs read, so no two surfaces
// can describe a plan differently. Columns resolve by tier NAME, with the list
// position as the fallback. The price row is intentionally omitted: the header
// shows the live, interval-aware price from the tier data.

export function TierSelector({ registrationEmail, onTierSelected, isLoading = false, directTierId = null, directInterval, onClose, forRegistration = false }: TierSelectorProps) {
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [selectedTierId, setSelectedTierId] = useState<number | null>(() => {
    const stored = localStorage.getItem("selectedTierId");
    return stored ? Number(stored) : null;
  });
  const [billingInterval, setBillingInterval] = useState<"month" | "year">("month");
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoResult, setPromoResult] = useState<PromoValidationResponse | null>(null);
  const [validatingPromo, setValidatingPromo] = useState(false);
  const [tooltipOpenId, setTooltipOpenId] = useState<number | null>(null);
  // In direct mode the whole component exists only to show the payment modal, so
  // open it from initial state. Relying on the effect below alone means a reused
  // instance (fast reopen, StrictMode, tier→tier without unmounting) never reopens.
  const [showPaymentModal, setShowPaymentModal] = useState(directTierId != null);
  // Payment Element state
  // Subscription created on the FIRST Subscribe click — cached so a declined
  // card retry re-confirms the same subscription instead of minting another
  // Stripe entry. Cleared whenever the tier/interval changes or the modal closes.
  const pendingIntentRef = useRef<{ clientSecret: string; subscriptionId: string } | null>(null);
  // Set once confirmPayment succeeds. If the host's registration/activation call
  // then fails (network drop, server error), the next Subscribe click re-submits
  // the ALREADY-PAID subscription to the host instead of charging again.
  const paidRef = useRef<{ tierId: number; subscriptionId: string } | null>(null);
  const stripeElementsRef = useRef<any>(null);
  const paymentElementRef = useRef<any>(null);
  const toast = useToast();

  useEffect(() => {
    const fetchTiers = async () => {
      try {
        const response = await api.get<Tier[]>("/lookups/tiers");
        setTiers(response);
        if (response.length > 0) {
          // Functional updater — never read selectedTierId from this async
          // closure (it's stale from mount). In direct mode ALWAYS honor the
          // explicitly requested tier; otherwise keep a valid current pick, else
          // fall back to the first tier.
          setSelectedTierId(prev => {
            if (directTierId != null) return directTierId;
            return prev !== null && response.some(t => t.id === prev) ? prev : response[0].id;
          });
        }
      } catch (error) {
        toast.error("Failed to load tier options");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchTiers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast]);

  useEffect(() => {
    if (selectedTierId !== null) localStorage.setItem("selectedTierId", String(selectedTierId));
  }, [selectedTierId]);

  // Direct mode: preselect the requested tier/interval and jump straight to the
  // payment modal, skipping the comparison table entirely.
  useEffect(() => {
    if (directTierId == null) return;
    setSelectedTierId(directTierId);
    if (directInterval) setBillingInterval(directInterval);
    setShowPaymentModal(true);
  }, [directTierId, directInterval]);

  const selectedTier = tiers.find(t => t.id === selectedTierId);
  const annualSupported = selectedTier?.amount_per_year != null;

  useEffect(() => {
    // Only downgrade once the tier is actually loaded. While `tiers` is still
    // fetching, `selectedTier` is undefined → `annualSupported` is false, and
    // without this guard a directInterval="year" request gets reverted to
    // "month" before the tier data ever arrives (and never restored).
    if (selectedTier && billingInterval === "year" && !annualSupported) {
      setBillingInterval("month");
    }
  }, [annualSupported, billingInterval, selectedTier]);

  // Manually picking a different tier clears any applied promo (it may belong to a
  // different tier). Applying a promo auto-switches the tier WITHOUT clearing itself,
  // so this is an explicit handler rather than an effect on selectedTierId.
  const handleSelectTier = (tierId: number) => {
    setSelectedTierId(tierId);
    setPromoResult(null);
    setPromoCode("");
  };

  // When the modal opens: mount the Payment Element in DEFERRED mode — no
  // backend/Stripe call happens here. The subscription (and its Stripe dashboard
  // entry) is only created when the buyer actually clicks Subscribe, so opening
  // and closing this modal no longer litters Stripe with incomplete subscriptions.
  useEffect(() => {
    if (!showPaymentModal || !selectedTierId || !selectedTier) return;
    if (billingInterval === "year" && selectedTier.amount_per_year == null) return;

    // First-charge amount for wallet displays; the real charge comes from the
    // Stripe price on the subscription created at pay time.
    const amount =
      billingInterval === "year" && selectedTier.amount_per_year != null
        ? selectedTier.amount_per_year
        : selectedTier.amount_per_month;

    // Stripe.js is injected async in main.tsx, and window.Stripe only becomes a
    // usable INSTANCE (has .elements) once its onload swaps the factory out. The
    // modal — especially direct mode, which opens on mount — can beat that, and
    // nothing re-runs this effect when the script lands, so a one-shot check
    // would fail permanently. Poll until ready, then mount the Payment Element
    // (supports cards, Apple Pay, Google Pay, Link, ACH, SEPA, etc.).
    let cancelled = false;
    const deadline = Date.now() + 12_000;
    let timer: ReturnType<typeof setTimeout>;
    const tryMount = () => {
      if (cancelled) return;
      const stripe = (window as any).Stripe;
      if (!stripe || typeof stripe.elements !== "function") {
        if (Date.now() > deadline) { toast.error("Stripe not loaded. Please refresh."); return; }
        timer = setTimeout(tryMount, 150);
        return;
      }
      const mountPoint = document.getElementById("payment-element");
      if (!mountPoint) return;
      mountPoint.innerHTML = "";

      const elements = stripe.elements({
        mode: "subscription",
        amount: Math.round(amount * 100),
        currency: "usd",
        // Gold-accented appearance following the active light/dark theme.
        appearance: stripeAppearance(),
      });

      const paymentEl = elements.create("payment", {
        // Accordion lists every payment method as a full-width row — all options stay
        // visible and it stays responsive on narrow screens (no horizontal overflow).
        layout: { type: "accordion", defaultCollapsed: false, radios: true, spacedAccordionItems: true },
      });
      paymentEl.mount(mountPoint);
      stripeElementsRef.current = elements;
      paymentElementRef.current = paymentEl;
    };
    timer = setTimeout(tryMount, 80);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (paymentElementRef.current) {
        try { paymentElementRef.current.unmount(); } catch { /* ignore */ }
        paymentElementRef.current = null;
      }
      stripeElementsRef.current = null;
      // Any subscription created at pay time was for the previous tier/interval —
      // forget it so the next attempt creates a fresh, correctly-priced one.
      pendingIntentRef.current = null;
    };
    // selectedTier is a dep (not just a guard): on a fresh mount `tiers` loads
    // async, so this effect first runs while selectedTier is undefined and bails.
    // It must re-run once the tier resolves, or the Payment Element never mounts.
  }, [showPaymentModal, selectedTierId, billingInterval, selectedTier]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleValidatePromo = async () => {
    if (!selectedTierId) { toast.error("Please select a tier first"); return; }
    if (!promoCode.trim()) { toast.error("Please enter a promo code"); return; }
    setValidatingPromo(true);
    try {
      const response = await api.post<PromoValidationResponse>("/payments/validate-promo-code", {
        code: promoCode.trim().toUpperCase(),
        tier_id: selectedTierId,
      });
      // The promo's own tier is authoritative — a code for a different tier than the
      // one currently selected is still valid, so switch the selection to the promo's
      // tier. Set the result AFTER, so it survives (manual tier clicks clear it).
      setSelectedTierId(response.tier_id);
      setPromoResult(response);
      const switched = response.tier_id !== selectedTierId;
      toast.success(
        switched
          ? `Promo applied — ${response.tier_name} plan, ${response.access_days} days access`
          : `Promo code applied: ${response.access_days} days access`
      );
    } catch (error) {
      setPromoResult(null);
      toast.error(error instanceof Error ? error.message : "Invalid promo code");
    } finally {
      setValidatingPromo(false);
    }
  };

  const handleContinueWithPromo = async () => {
    if (!promoResult) { toast.error("Apply a valid promo code first"); return; }
    // Redeem against the promo's tier, never the previously selected one.
    onTierSelected({ tierId: promoResult.tier_id, promoCode: promoResult.code });
  };

  // Closing the payment modal in direct mode dismisses the whole flow.
  const closePaymentModal = () => {
    setShowPaymentModal(false);
    if (directTierId != null) onClose?.();
  };

  const handlePayment = async () => {
    if (!selectedTierId) { toast.error("Payment form not ready. Please wait."); return; }

    // A previous attempt already charged this tier but registration/activation
    // failed afterwards — hand the paid subscription back to the host, never
    // touch Stripe again (re-confirming a succeeded PaymentIntent errors, and
    // creating a new subscription would double-charge).
    if (paidRef.current && paidRef.current.tierId === selectedTierId) {
      onTierSelected({ tierId: selectedTierId, subscriptionId: paidRef.current.subscriptionId });
      return;
    }

    const stripe = (window as any).Stripe;
    if (!stripe) { toast.error("Stripe not loaded. Please refresh."); return; }
    const elements = stripeElementsRef.current;
    if (!elements) { toast.error("Payment form not ready. Please wait."); return; }

    setProcessingPayment(true);
    try {
      // Deferred-intent flow: validate the payment form BEFORE anything touches
      // Stripe's backend (the element renders its own validation messages).
      const { error: submitError } = await elements.submit();
      if (submitError) return;

      // Only now create the subscription — abandoned modals never reach Stripe.
      // A retry after a decline reuses the same subscription instead of minting
      // another incomplete entry.
      let intent = pendingIntentRef.current;
      if (!intent) {
        const subResponse = await api.post<{ client_secret: string; subscription_id: string }>("/payments/create-subscription", {
          email: registrationEmail,
          tier_id: selectedTierId,
          interval: billingInterval,
          for_registration: forRegistration || undefined,
        });
        intent = { clientSecret: subResponse.client_secret, subscriptionId: subResponse.subscription_id };
        pendingIntentRef.current = intent;
      }

      // Redirect-based payment methods navigate away and wipe React state — stash
      // enough context for the host page to finish the flow when Stripe returns
      // the browser to return_url with ?redirect_status=….
      sessionStorage.setItem(
        PENDING_STRIPE_PAYMENT_KEY,
        JSON.stringify({ subscriptionId: intent.subscriptionId, tierId: selectedTierId } satisfies PendingStripePayment),
      );

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret: intent.clientSecret,
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      });

      if (error) {
        // No redirect happened (decline/validation) — the stash is stale.
        sessionStorage.removeItem(PENDING_STRIPE_PAYMENT_KEY);
        toast.error(error.message ?? "Payment failed. Please try again.");
      } else if (paymentIntent && (paymentIntent.status === "succeeded" || paymentIntent.status === "processing")) {
        // "processing" is valid for bank debits (ACH, SEPA) — subscription is active
        paidRef.current = { tierId: selectedTierId, subscriptionId: intent.subscriptionId };
        onTierSelected({ tierId: selectedTierId, subscriptionId: intent.subscriptionId });
      }
    } catch (err) {
      sessionStorage.removeItem(PENDING_STRIPE_PAYMENT_KEY);
      toast.error(err instanceof Error ? err.message : "Payment failed. Please try again.");
    } finally {
      setProcessingPayment(false);
    }
  };

  if (loading && directTierId != null) {
    // Direct mode: brief full-screen spinner until the payment modal is ready.
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}>
        <Loader2 className="h-9 w-9 animate-spin text-white" />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4 text-muted-foreground">
        <Loader2 className="h-9 w-9 animate-spin text-primary" />
        <span className="text-sm font-medium">Loading plans...</span>
      </div>
    );
  }

  const isAnnual = billingInterval === "year";
  // Amount + suffix actually shown. In annual mode we show ONLY the yearly figure
  // (e.g. "$588/yr"), not a per-month equivalent.
  const cycleAmount = (t: Tier) => (isAnnual && t.amount_per_year ? t.amount_per_year : t.amount_per_month);
  const cycleSuffix = isAnnual ? "/yr" : "/mo";
  // Annual savings vs paying monthly for a year, for the "Save N%" badge.
  const annualSavingsPct = (t: Tier) =>
    t.amount_per_year ? Math.round((1 - t.amount_per_year / (t.amount_per_month * 12)) * 100) : 0;
  const maxAnnualSavings = tiers.reduce((m, t) => Math.max(m, annualSavingsPct(t)), 0);

  // Billing schedule (so the buyer sees exactly when/what they'll be charged).
  const fmtDate = (d: Date) => d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  // Anniversary billing: the full price is charged on the start date, then the SAME
  // calendar date every cycle — no proration, no 1st-of-month anchor.
  const addInterval = (d: Date) => {
    const n = new Date(d);
    if (isAnnual) n.setFullYear(n.getFullYear() + 1);
    else n.setMonth(n.getMonth() + 1);
    return n;
  };
  // Recurring renewal date shown to the buyer — anchored to today's charge.
  const anchorDate = addInterval(new Date());
  // Every charge is the full cycle price — no proration.
  const estChargeNow = selectedTier ? cycleAmount(selectedTier) : 0;

  return (
    <div className="w-full">
      {directTierId == null && (
      <>
      {/* ── Billing interval toggle ─────────────────────────────── */}
      <div className="flex flex-col items-center gap-2 mb-4">
        <div className="inline-flex items-center rounded-full border border-border bg-slate-50 p-1">
          <button
            type="button"
            onClick={() => setBillingInterval("month")}
            className={`px-6 py-1.5 text-sm font-semibold rounded-full transition-colors ${billingInterval === "month" ? "bg-white text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => annualSupported && setBillingInterval("year")}
            disabled={!annualSupported}
            title={!annualSupported ? "Annual billing is unavailable for this plan." : undefined}
            className={`px-6 py-1.5 text-sm font-semibold rounded-full transition-colors flex items-center gap-2 ${billingInterval === "year" ? "bg-white text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"} ${!annualSupported ? "opacity-40 cursor-not-allowed" : ""}`}
          >
            Annual
            {maxAnnualSavings > 0 && (
              <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">
                Save up to {maxAnnualSavings}%
              </span>
            )}
          </button>
        </div>
      </div>
      {!annualSupported && selectedTier && (
        <p className="text-xs text-muted-foreground mb-4">
          Annual billing is unavailable for the selected plan.
        </p>
      )}

      <div className="flex flex-col xl:flex-row gap-6 items-start">

        {/* ── LEFT: Plan comparison table ─────────────────────────── */}
        <div className="flex-1 min-w-0 rounded-2xl border border-border bg-white shadow-sm">
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[460px] table-fixed border-collapse">

            {/* ── Column group widths ── */}
            <colgroup>
              <col style={{ width: "20%" }} />
              {tiers.map((_, i) => (
                <col key={i} style={{ width: `${80 / tiers.length}%` }} />
              ))}
            </colgroup>

            {/* ── Tier header row ── */}
            <thead>
              <tr>
                {/* Feature label cell */}
                <th className="px-4 py-3 text-left align-bottom bg-slate-50 border-b border-border">
                  <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Feature</span>
                </th>

                {tiers.map((tier, i) => {
                  const meta = TIER_META[tierColumnIndex(tier.name, i)] ?? TIER_META[0];
                  const isSelected = selectedTierId === tier.id;

                  return (
                    <th
                      key={tier.id}
                      onClick={() => handleSelectTier(tier.id)}
                      className={`relative px-2 py-2.5 text-center align-top cursor-pointer select-none border-b transition-colors duration-150
                        ${isSelected
                          ? `${meta.colBg} border-b-2 ${meta.colBorder}`
                          : "bg-white border-border hover:bg-slate-50/70"
                        }`}
                    >
                      {/* Badge (inline, not absolute) */}
                      <div className="h-5 flex items-center justify-center mb-1">
                        {meta.badge && (
                          <div className={`text-[10px] font-bold px-3 py-0.5 rounded-full text-white whitespace-nowrap shadow ${meta.badgeBg}`}>
                            {meta.badge}
                          </div>
                        )}
                      </div>

                      {/* Info button */}
                      <div className="absolute top-3 right-3">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setTooltipOpenId(tooltipOpenId === tier.id ? null : tier.id); }}
                          className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-border bg-white text-muted-foreground hover:text-foreground shadow-sm"
                        >
                          <Info className="h-3 w-3" />
                        </button>
                        {tooltipOpenId === tier.id && (
                          <div
                            className="absolute right-0 top-7 z-50 w-56 rounded-xl border border-border bg-white p-3.5 shadow-2xl text-xs text-left"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold text-foreground">{tier.name}</span>
                              <button onClick={() => setTooltipOpenId(null)} className="text-muted-foreground hover:text-foreground"><X className="h-3 w-3" /></button>
                            </div>
                            <p className="text-muted-foreground whitespace-pre-line leading-relaxed">{tier.features_text?.trim() || "No additional details."}</p>
                          </div>
                        )}
                      </div>

                      {/* Tier number chip */}
                      <div className={`inline-flex items-center justify-center mb-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold border
                        ${isSelected ? `${meta.activeBg} text-white border-transparent` : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                        TIER {i + 1}
                      </div>

                      {/* Icon + label */}
                      <div className="flex flex-col items-center gap-0.5">
                        <div className={`inline-flex h-7 w-7 items-center justify-center rounded-xl ${meta.iconBg}`}>
                          {meta.icon}
                        </div>
                        <span className={`text-[9px] font-bold uppercase tracking-wider ${isSelected ? meta.accent : "text-muted-foreground"}`}>
                          {meta.label}
                        </span>
                        <span className="text-sm font-bold text-foreground leading-tight text-center">{meta.subtitle}</span>
                        <div className="mt-0.5">
                          <span className="text-xl font-extrabold text-foreground">${cycleAmount(tier).toLocaleString()}</span>
                          <span className="text-xs text-muted-foreground font-medium ml-0.5">{cycleSuffix}</span>
                        </div>
                      </div>

                      {/* Select button */}
                      <div className="mt-2 px-2">
                        <div className={`w-full rounded-lg py-1.5 text-xs font-semibold flex items-center justify-center gap-1 transition-all
                          ${isSelected
                            ? `${meta.activeBg} text-white shadow`
                            : "border border-border text-muted-foreground hover:border-slate-400 hover:text-foreground bg-white"
                          }`}
                        >
                          {isSelected && <Check className="h-3.5 w-3.5" />}
                          {isSelected ? "Selected" : "Select"}
                        </div>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            {/* ── Feature rows ── */}
            <tbody>
              {PLAN_COMPARE_COMPACT.map((row, rowIdx) => (
                <tr
                  key={rowIdx}
                  className={`border-b border-border/50 transition-colors hover:bg-slate-50/60 ${rowIdx % 2 === 0 ? "bg-white" : "bg-slate-50/30"}`}
                >
                  <td className="px-4 py-2.5 text-sm font-medium text-foreground bg-slate-50/40">
                    {row.feature}
                  </td>

                  {tiers.map((tier, colIdx) => {
                    // Name first, list position as the fallback — a renamed or
                    // reordered tier must never inherit another plan's column.
                    const col = tierColumnIndex(tier.name, colIdx);
                    const meta = TIER_META[col] ?? TIER_META[0];
                    const isSelected = selectedTierId === tier.id;
                    const val = row.values[col];

                    return (
                      <td
                        key={tier.id}
                        onClick={() => handleSelectTier(tier.id)}
                        className={`px-2 py-2 text-center cursor-pointer transition-colors duration-100
                          ${isSelected ? `${meta.colBg} border-x ${meta.colBorder} border-opacity-30` : ""}`}
                      >
                        {val === true ? (
                          <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full mx-auto ${isSelected ? `${meta.activeBg} text-white` : "bg-slate-200 text-slate-600"}`}>
                            <Check className="h-3 w-3" />
                          </span>
                        ) : val === false ? (
                          <span className="text-muted-foreground/30 text-base font-light select-none">—</span>
                        ) : (
                          <span className={`text-xs font-medium ${isSelected ? meta.accent : "text-muted-foreground"}`}>{val}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}

              {/* Bottom CTA row */}
              <tr className="bg-slate-50/40">
                <td className="px-4 py-3" />
                {tiers.map((tier, i) => {
                  const meta = TIER_META[tierColumnIndex(tier.name, i)] ?? TIER_META[0];
                  const isSelected = selectedTierId === tier.id;
                  return (
                    <td key={tier.id} className={`px-2 py-3 text-center ${isSelected ? `${meta.colBg} border-x ${meta.colBorder} border-opacity-30` : ""}`}>
                      <button
                        onClick={() => handleSelectTier(tier.id)}
                        className={`w-full rounded-xl py-2 text-xs font-bold transition-all duration-150 flex items-center justify-center gap-1
                          ${isSelected
                            ? `${meta.activeBg} text-white shadow-md`
                            : "border-2 border-border text-foreground hover:border-slate-400 bg-white"
                          }`}
                      >
                        {isSelected && <Check className="h-3.5 w-3.5" />}
                        {isSelected ? "Selected" : "Select"}
                      </button>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
        </div>{/* end left panel */}

        {/* ── RIGHT: Payment panel ─────────────────────────────────── */}
        <div className="w-full xl:w-[360px] shrink-0 sticky top-6">
          {selectedTierId && selectedTier ? (
            <div className="rounded-2xl border border-border bg-white shadow-sm overflow-hidden">

              {/* Header */}
              <div className="px-5 py-4 border-b border-border bg-slate-50/70">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="font-bold text-foreground text-base">Complete Payment</h4>
                  <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
                    <span>Stripe</span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  {selectedTier.name}
                  <span className="mx-1.5 text-border">·</span>
                  <span className="font-semibold text-foreground">${cycleAmount(selectedTier).toLocaleString()}{cycleSuffix}</span>
                </p>
              </div>

              {/* Order summary */}
              <div className="px-5 py-4 border-b border-border bg-slate-50/30 space-y-2">
                {[
                  ["Plan", selectedTier.name],
                  ["Billing", isAnnual ? "Annual" : "Monthly"],
                  ...(selectedTier.free_listings_access ? [["Unlocks", `${selectedTier.free_listings_access}/mo`]] : []),
                ].map(([label, val]) => (
                  <div key={label} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{label}</span>
                    <span className="font-medium text-foreground">{val}</span>
                  </div>
                ))}
                <div className="flex justify-between items-baseline pt-2 border-t border-border mt-2">
                  <span className="text-sm font-semibold text-foreground">Due today</span>
                  <span className="text-lg font-extrabold text-foreground">${(isAnnual ? cycleAmount(selectedTier) : estChargeNow).toLocaleString()}</span>
                </div>
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  {isAnnual ? (
                    <>Billed <span className="font-medium text-foreground">${cycleAmount(selectedTier).toLocaleString()}/yr</span> today, auto-renewing on the same date each year. Cancel anytime.</>
                  ) : (
                    <><span className="font-medium text-foreground">${estChargeNow.toLocaleString()}</span> today, then <span className="font-medium text-foreground">${cycleAmount(selectedTier).toLocaleString()}/mo</span> on the same date every month. Cancel anytime.</>
                  )}
                </p>
              </div>

              {/* Primary action — kept up top so it's visible without scrolling */}
              {!promoResult && (
                <div className="px-5 py-4 border-b border-border space-y-2">
                  <Button
                    onClick={() => setShowPaymentModal(true)}
                    size="lg"
                    className="w-full h-12 text-sm font-bold rounded-xl shadow-md bg-primary hover:bg-primary/90 flex items-center justify-center gap-2"
                  >
                    <Lock className="h-4 w-4" />
                    {`Pay Now — $${cycleAmount(selectedTier).toLocaleString()}${isAnnual ? "/yr" : "/mo"}`}
                  </Button>
                  <p className="text-center text-[11px] text-muted-foreground leading-relaxed">
                    <ShieldCheck className="inline h-3 w-3 text-emerald-500 mr-1" />
                    Secure payment · Cancel anytime
                  </p>
                </div>
              )}

              {/* What's included */}
              {(selectedTier.features_text?.trim().split("\n").filter(Boolean) ?? []).length > 0 && (
                <div className="px-5 py-4 border-b border-border">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2.5">What's included</p>
                  <ul className="space-y-1.5">
                    {(selectedTier.features_text?.trim().split("\n").filter(Boolean) ?? []).map((feat, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                        <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Payment form */}
              <div className="px-5 py-4 space-y-4">
                {/* Promo Code */}
                <div className="rounded-xl border border-dashed border-border bg-slate-50/50 p-3">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-foreground mb-2">
                    <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                    Promo code
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      placeholder="ENTER CODE"
                      className="font-mono tracking-widest text-sm bg-white h-8 text-xs text-slate-900 placeholder:text-slate-400"
                      disabled={!!promoResult}
                    />
                    {promoResult ? (
                      <Button type="button" variant="outline" size="sm" onClick={() => { setPromoResult(null); setPromoCode(""); }} className="shrink-0 h-8 text-xs">Remove</Button>
                    ) : (
                      <Button type="button" variant="outline" size="sm" onClick={handleValidatePromo} disabled={validatingPromo || !promoCode.trim()} className="shrink-0 h-8 text-xs">
                        {validatingPromo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Apply"}
                      </Button>
                    )}
                  </div>

                  {promoResult && (
                    <div className="mt-2.5 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-xs text-emerald-800">
                      <Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <span className="font-semibold">{promoResult.code}</span> — {promoResult.access_days} days access
                        <div className="mt-2">
                          <Button type="button" size="sm" onClick={handleContinueWithPromo} disabled={isLoading} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs">
                            {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                            Continue with promo
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-border bg-slate-50/40 p-8 text-center">
              <p className="text-sm text-muted-foreground">Select a plan from the table to continue with payment.</p>
            </div>
          )}

        </div>{/* end right panel */}

      </div>{/* end flex row */}
      </>
      )}

      {/* ── Payment Modal ─────────────────────────────────────────── */}
      {showPaymentModal && selectedTier && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
          style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
          onClick={(e) => { if (e.target === e.currentTarget) closePaymentModal(); }}
        >
          <div
            className="relative flex w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-200"
            style={{ maxHeight: "calc(100vh / var(--app-zoom) - 3rem)" }}
          >

            {/* Modal header */}
            <div className="flex items-center justify-between px-5 sm:px-7 pt-4 pb-3.5 border-b border-border shrink-0">
              <BrandLogo className="h-7 w-auto object-contain" />
              {!isLoading && !processingPayment && (
                <button
                  onClick={closePaymentModal}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border hover:bg-slate-100 text-muted-foreground transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Account creation loader */}
            {isLoading && (
              <div className="flex flex-col items-center justify-center px-8 py-12 gap-7">
                <div className="relative flex items-center justify-center">
                  <div className="absolute h-24 w-24 rounded-full bg-primary/8 animate-ping" style={{ animationDuration: "1.8s" }} />
                  <div className="absolute rounded-full bg-primary/12" style={{ height: 72, width: 72 }} />
                  <div className="relative z-10 h-16 w-16 rounded-full bg-primary flex items-center justify-center shadow-lg shadow-primary/30">
                    <Loader2 className="h-8 w-8 text-white animate-spin" />
                  </div>
                </div>
                <div className="text-center space-y-1.5">
                  <p className="text-lg font-bold text-foreground">Setting up your account</p>
                  <p className="text-sm text-muted-foreground">
                    Payment confirmed — please don't close this window.
                  </p>
                </div>
                <div className="w-full space-y-2.5">
                  {[
                    { label: "Payment verified", done: true },
                    { label: "Creating your subscription", done: false },
                    { label: "Activating account access", done: false },
                  ].map(({ label, done }) => (
                    <div key={label} className="flex items-center gap-3 rounded-xl border border-border bg-slate-50/60 px-4 py-2.5">
                      <div className={`h-5 w-5 rounded-full flex items-center justify-center shrink-0 ${done ? "bg-emerald-500" : "bg-primary/15"}`}>
                        {done ? <Check className="h-3 w-3 text-white" /> : <div className="h-2.5 w-2.5 rounded-full bg-primary animate-pulse" />}
                      </div>
                      <span className={`text-sm font-medium ${done ? "text-emerald-700" : "text-foreground"}`}>{label}</span>
                    </div>
                  ))}
                </div>
                <div className="w-full h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-primary rounded-full animate-pulse" style={{ width: "65%" }} />
                </div>
              </div>
            )}

            {/* Two-column body: left = order summary, right = payment form */}
            {!isLoading && (
              <>
              {/* Scrollable content region — billing notice + order summary + payment form */}
              <div className="flex-1 min-h-0 overflow-y-auto">
              {/* Billing notice — on top, full width */}
              <div className="border-b border-border px-4 sm:px-6 py-3">
                <div className="flex items-start gap-2.5 rounded-xl bg-amber-50 border border-amber-200 px-3.5 py-3 text-xs text-amber-800">
                  <Info className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                  {isAnnual ? (
                    <p>
                      <span className="font-semibold">Auto-renewal enabled.</span> You'll be charged{" "}
                      <span className="font-semibold">${cycleAmount(selectedTier).toLocaleString()}</span> today, then every year. Cancel anytime from account settings.
                    </p>
                  ) : (
                    <p>
                      <span className="font-semibold">Charged today:</span>{" "}
                      <span className="font-semibold">${estChargeNow.toLocaleString()}</span>, then{" "}
                      <span className="font-semibold">${cycleAmount(selectedTier).toLocaleString()}/month</span> on the same date ({fmtDate(anchorDate)}). Cancel anytime.
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-col md:flex-row">

                {/* ── Left: Order summary ── */}
                <div className="md:w-[44%] shrink-0 bg-slate-50/70 border-b md:border-b-0 md:border-r border-border px-4 sm:px-6 py-4 sm:py-5 flex flex-col gap-3 sm:gap-4">

                  {/* Plan card */}
                  <div className="rounded-xl border border-border bg-white p-4 shadow-sm">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Selected plan</p>
                    <div className="flex items-start justify-between">
                      <p className="text-xl font-extrabold text-foreground leading-tight">{selectedTier.name}</p>
                      <div className="text-right shrink-0 ml-2">
                        <p className="text-2xl font-extrabold text-foreground leading-none">${cycleAmount(selectedTier).toLocaleString()}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">{isAnnual ? "per year" : "per month"}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">
                        <RefreshCw className="h-2.5 w-2.5" />{isAnnual ? "Auto-renews yearly" : "Auto-renews monthly"}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">
                        <ShieldCheck className="h-2.5 w-2.5" />Cancel anytime
                      </span>
                    </div>
                  </div>

                  {/* Order line items */}
                  <div className="rounded-xl border border-border bg-white overflow-hidden shadow-sm">
                    <div className="divide-y divide-border">
                      {[
                        ["Plan", selectedTier.name],
                        ["Billing", isAnnual ? "Annual" : "Monthly"],
                        ...(selectedTier.free_listings_access ? [["Unlocks", `${selectedTier.free_listings_access}/mo`]] : []),
                      ].map(([label, val]) => (
                        <div key={label} className="flex justify-between items-center px-4 py-2.5 text-sm">
                          <span className="text-muted-foreground">{label}</span>
                          <span className="font-medium text-foreground">{val}</span>
                        </div>
                      ))}
                      <div className="flex justify-between items-center px-4 py-3 bg-slate-50/60">
                        <span className="text-sm font-bold text-foreground">Due today</span>
                        <span className="text-lg font-extrabold text-foreground">${(isAnnual ? cycleAmount(selectedTier) : estChargeNow).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Promo code — direct payment flow only */}
                  {directTierId != null && (
                    <div className="rounded-xl border border-dashed border-border bg-white p-3 shadow-sm">
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground mb-2">
                        <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                        Have a promo code?
                      </div>
                      <div className="flex gap-2">
                        <Input
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                          placeholder="ENTER CODE"
                          className="font-mono tracking-widest text-xs bg-white h-8 text-slate-900 placeholder:text-slate-400"
                          disabled={!!promoResult}
                        />
                        {promoResult ? (
                          <Button type="button" variant="outline" size="sm" onClick={() => { setPromoResult(null); setPromoCode(""); }} className="shrink-0 h-8 text-xs">Remove</Button>
                        ) : (
                          <Button type="button" variant="outline" size="sm" onClick={handleValidatePromo} disabled={validatingPromo || !promoCode.trim()} className="shrink-0 h-8 text-xs">
                            {validatingPromo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Apply"}
                          </Button>
                        )}
                      </div>
                      {promoResult && (
                        <div className="mt-2.5 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-xs text-emerald-800">
                          <Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
                          <div className="flex-1">
                            <span className="font-semibold">{promoResult.code}</span> — {promoResult.access_days} days access
                            <div className="mt-2">
                              <Button type="button" size="sm" onClick={handleContinueWithPromo} disabled={isLoading} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs">
                                {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                                Redeem with promo
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Features */}
                  {(selectedTier.features_text?.trim().split("\n").filter(Boolean) ?? []).length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">What's included</p>
                      <ul className="space-y-1.5">
                        {(selectedTier.features_text?.trim().split("\n").filter(Boolean) ?? []).map((feat, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                            <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                            <span>{feat}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* ── Right: Payment form (card only — fills the column) ── */}
                <div className="flex-1 px-4 sm:px-6 py-4 sm:py-5 flex flex-col gap-3">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Payment details</p>
                  <div className="flex-1">
                    {/* The Stripe element mounts here ~80ms after the modal opens
                        (deferred mode — no network round-trip needed first). */}
                    <div id="payment-element" />
                  </div>
                </div>
              </div>
              </div>

              {/* ── Full-width sticky footer: prominent subscribe button + notes ── */}
              <div className="shrink-0 border-t border-border bg-white px-4 sm:px-7 py-3 sm:py-4 space-y-2 sm:space-y-2.5">
                <Button
                  onClick={handlePayment}
                  disabled={processingPayment || isLoading}
                  size="lg"
                  className="w-full h-12 sm:h-14 text-sm sm:text-base font-extrabold rounded-xl shadow-md"
                >
                  {processingPayment ? (
                    <><Loader2 className="h-5 w-5 animate-spin mr-2 shrink-0" /><span className="truncate">Processing payment…</span></>
                  ) : (
                    <><Lock className="h-4 w-4 sm:h-5 sm:w-5 mr-2 shrink-0" /><span className="truncate">{`Subscribe — $${cycleAmount(selectedTier).toLocaleString()}${isAnnual ? "/yr" : "/mo"}`}</span></>
                  )}
                </Button>
                <p className="text-center text-[11px] sm:text-xs text-emerald-700 font-medium leading-snug">
                  Charged today · cancel anytime — no penalties, no questions asked.
                </p>
                <div className="flex items-center justify-center gap-3 sm:gap-4 text-[10px] sm:text-[11px] text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
                    256-bit encryption
                  </span>
                  <span className="text-border">|</span>
                  <span className="flex items-center gap-1">
                    <Lock className="h-3 w-3" />
                    Powered by Stripe
                  </span>
                </div>
              </div>
              </>
            )}

          </div>
        </div>
      )}

    </div>
  );
}
